import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import authService from '../services/authService';
import { User, LoginCredentials, AuthContextType } from '../types/auth.types';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // ==========================================
  // CHECK AUTH ON MOUNT (PERSISTENT LOGIN)
  // ==========================================
  useEffect(() => {
    initializeAuth();
  }, []);

  const initializeAuth = async () => {
    console.log('🔍 Initializing authentication...');
    
    try {
      setIsLoading(true);
      
      const { accessToken, refreshToken, user: savedUser } = authService.getAuthData();

      console.log('📦 Stored auth data:', {
        hasAccessToken: !!accessToken,
        hasRefreshToken: !!refreshToken,
        hasUser: !!savedUser,
      });

      if (!accessToken || !savedUser) {
        console.log('ℹ️ No stored auth data, user needs to login');
        setUser(null);
        setIsLoading(false);
        setIsInitialized(true);
        return;
      }

      // Check if token is expired
      if (authService.isTokenExpired(accessToken)) {
        console.log('⏰ Access token expired, attempting refresh...');
        
        if (refreshToken) {
          try {
            const newAccessToken = await authService.refreshToken(refreshToken);
            localStorage.setItem('accessToken', newAccessToken);
            console.log('✅ Token refreshed successfully');
            
            // Fetch user with new token
            const currentUser = await authService.getCurrentUser();
            setUser(currentUser);
            
            // Update stored user data
            authService.saveAuthData(newAccessToken, refreshToken, currentUser);
            
            console.log('✅ User authenticated:', currentUser.name);
          } catch (refreshError: any) {
            console.error('❌ Token refresh failed:', refreshError.message);
            authService.clearAuthData();
            setUser(null);
            
            // Only show toast if not on login page
            if (location.pathname !== '/login') {
              toast.error('Session expired. Please login again.');
            }
          }
        } else {
          console.log('❌ No refresh token available');
          authService.clearAuthData();
          setUser(null);
        }
      } else {
        // Token still valid, verify with backend
        console.log('✅ Access token valid, verifying with server...');
        
        try {
          const currentUser = await authService.getCurrentUser();
          setUser(currentUser);
          console.log('✅ User authenticated:', currentUser.name);
        } catch (error: any) {
          console.error('❌ Failed to verify user:', error.message);
          
          // Try to refresh token
          if (refreshToken) {
            try {
              console.log('🔄 Attempting token refresh...');
              const newAccessToken = await authService.refreshToken(refreshToken);
              localStorage.setItem('accessToken', newAccessToken);
              
              const currentUser = await authService.getCurrentUser();
              setUser(currentUser);
              
              authService.saveAuthData(newAccessToken, refreshToken, currentUser);
              console.log('✅ Token refreshed and user verified');
            } catch (refreshError) {
              console.error('❌ Token refresh failed:', refreshError);
              authService.clearAuthData();
              setUser(null);
            }
          } else {
            authService.clearAuthData();
            setUser(null);
          }
        }
      }
    } catch (error) {
      console.error('❌ Auth initialization failed:', error);
      authService.clearAuthData();
      setUser(null);
    } finally {
      setIsLoading(false);
      setIsInitialized(true);
      console.log('✅ Auth initialization complete');
    }
  };

  const login = async (credentials: LoginCredentials) => {
    try {
      setIsLoading(true);
      console.log('🔐 Attempting login...');
      
      const response = await authService.login(credentials);
      const { user, tokens } = response.data;

      // Save auth data to localStorage
      authService.saveAuthData(tokens.accessToken, tokens.refreshToken, user);

      setUser(user);

      console.log('✅ Login successful:', user.name);
      toast.success(`Welcome back, ${user.name}! 👋`);

      // Redirect to intended page or dashboard
      const from = (location.state as any)?.from?.pathname || '/dashboard';
      navigate(from, { replace: true });
    } catch (error: any) {
      console.error('❌ Login failed:', error);
      const errorMessage = error.response?.data?.message || 'Login failed. Please try again.';
      toast.error(errorMessage);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      console.log('🚪 Logging out...');
      await authService.logout();
      setUser(null);
      toast.success('Logged out successfully');
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('❌ Logout error:', error);
      // Clear data anyway
      authService.clearAuthData();
      setUser(null);
      navigate('/login', { replace: true });
    }
  };

  const refreshToken = async () => {
    try {
      const { refreshToken: token } = authService.getAuthData();

      if (token) {
        const newAccessToken = await authService.refreshToken(token);
        localStorage.setItem('accessToken', newAccessToken);
        console.log('✅ Token refreshed in background');
      }
    } catch (error) {
      console.error('❌ Background token refresh failed:', error);
      logout();
    }
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    refreshToken,
  };

  // Show loader until auth check is complete
  if (!isInitialized) {
    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
          zIndex: 9999,
        }}
      >
        <div style={{ textAlign: 'center', color: 'white' }}>
          <div
            style={{
              width: '60px',
              height: '60px',
              border: '4px solid rgba(255, 255, 255, 0.2)',
              borderTop: '4px solid #3B82F6',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 1rem',
            }}
          ></div>
          <p style={{ fontSize: '1rem', opacity: 0.9 }}>
            Verifying authentication...
          </p>
        </div>
      </div>
    );
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
};